import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { TaskWorkspaceStore } from '../../../core/h3ml/state/task-workspace-store';
import { truncateToPoint2D } from '../../../cross-team-shared/misc-utils';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { PointCloudOutput } from '../../../team-apis/otterdam/outputs/point-cloud-output';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { planeEquationGenerator } from '../../test-helpers/builders/point-cloud-buffer-attributes-equations-generators';
import { CloudAttributes } from '../../utils/kd-tree/cloud-attributes.service';
import { PointCloudAssetService } from '../../utils/loading/point-cloud-asset.service';
import { addPoints, subtractPoints } from '../../utils/point-utils';
import {
  buildInstanceIds,
  buildIntensities,
  buildPointCloudPositions,
  buildSourceIds,
  sortPoints,
} from './generate-random-points-helpers';

@Component({
  selector: 'h3-generate-random-points',
  templateUrl: './generate-random-points.component.html',
  styleUrls: ['./generate-random-points.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GenerateRandomPointsComponent implements OnInit {
  cloudAttributes = [
    {
      name: 'intensity',
      value: false,
    },
  ];
  minPoint: Point2D = [-10, -10];
  maxPoint: Point2D = [10, 10];
  totalPoints = 2000;
  useSensorLocation = false;

  private cloudZPlane = 2;
  private groundZPlane = 0;

  private pointCloudAssetService = inject(PointCloudAssetService);
  private store = inject(PointCloudContentStore);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);

  ngOnInit() {
    if (!this.store.state().output.meta.multi_point_cloud?.enabled) {
      this.cloudAttributes.push({
        name: 'Sensor ID (Multi Point-cloud)',
        value: false,
      });
    }
  }

  createPoints() {
    const instanceId = buildInstanceIds(
      this.totalPoints / 2,
      this.totalPoints / 2,
    );

    const position = this.getPositions();
    const sourceId = this.getSourceId();
    if (sourceId) {
      this.setUpMultiPointCloud();
    }

    const cloudAttributes: CloudAttributes = {
      instanceId,
      position,
      sourceId,
      intensity: this.cloudAttributes[0].value
        ? buildIntensities(this.totalPoints)
        : undefined,
    };

    this.updateCloud(cloudAttributes);
  }

  sortExistingPoints() {
    const frameNumber = this.store.state().render.frameNumber;
    const cloudAsset = this.pointCloudAssetService.getAsset(frameNumber)!;
    const sortedCloudAttributes = sortPoints(cloudAsset);

    this.updateCloud(sortedCloudAttributes);
  }

  private getPositions() {
    let minPoint = this.minPoint;
    let maxPoint = this.maxPoint;
    let groundZ = this.groundZPlane;
    let cloudZ = this.cloudZPlane;
    const { locations } = this.store.state();
    if (this.useSensorLocation && locations) {
      const { position } = locations[this.store.state().render.frameNumber];
      minPoint = subtractPoints(
        truncateToPoint2D(position),
        minPoint.map(Math.abs) as Point2D,
      );
      maxPoint = addPoints(
        truncateToPoint2D(position),
        maxPoint.map(Math.abs) as Point2D,
      );
      groundZ = position[2];
      cloudZ += groundZ;
    }

    return buildPointCloudPositions(undefined, {
      minPoint,
      maxPoint,
      totalCloudPoints: this.totalPoints / 2,
      totalGroundPoints: this.totalPoints / 2,
      cloudZFunc: planeEquationGenerator(cloudZ),
      groundZFunc: planeEquationGenerator(groundZ),
    });
  }

  private getSourceId() {
    return this.shouldUseMultiPointCloud()
      ? buildSourceIds(this.totalPoints)
      : undefined;
  }

  private setUpMultiPointCloud() {
    if (!this.shouldUseMultiPointCloud()) {
      return;
    }

    if (!this.store.state().output.meta.multi_point_cloud?.enabled) {
      const multiPointCloud = {
        enabled: true,
        sensors: [0, 1, 2].map((source_id) => ({
          source_id,
          label: `Sensor ${source_id}`,
        })),
      };
      this.store('output')('meta')('multi_point_cloud').set(multiPointCloud);
      const outputId = this.store.state().output.id;
      const { project } = this.taskWorkspaceStore.state();
      const outputIndex = project.outputs.findIndex(
        (output) => output.id === outputId,
      );
      const coreOutputStore: Store<PointCloudOutput> =
        this.taskWorkspaceStore('project')('outputs')(outputIndex);
      coreOutputStore('meta')('multi_point_cloud').set(multiPointCloud);

      this.store('render')('layerSettings')('pointAppearanceBySensor').set({
        0: [255, 0, 0, 255],
        1: [0, 255, 0, 255],
        2: [0, 0, 255, 255],
      });
    }
  }

  private shouldUseMultiPointCloud() {
    return this.cloudAttributes[1]?.value;
  }

  private updateCloud(cloudAttributes: CloudAttributes) {
    const { frameNumber } = this.store.state().render;

    Object.assign(
      this.pointCloudAssetService.getAsset(frameNumber)!,
      cloudAttributes,
    );

    // dev-only component hack: force point cloud update
    this.store('render')('frameNumber').setUsing((n) => n + 1);
    this.store('render')('frameNumber').setUsing((n) => n - 1);
  }
}
